import { useContext, useEffect, useState } from "react";
import { GeaoContext } from "../../GeaoContextProvider";
import InterviewStatus from "../../constants/InterviewStatus";
import Role from "../../constants/Role";
import { initialize } from "../../../../api/onboarding";
import { getAnswer, objectToQuestionAnswers } from "../../mappers/InterviewMapper";
import { HttpStatusError } from "../../../../util/api-utils";

/**
 * Custom hook for managing GEAO onboarding interview initialization process.
 * 
 * This hook handles the initialization of agency and agent interviews based on form data,
 * managing various error states, duplicate validations, and NIPR name change scenarios.
 * It coordinates the interview flow for both Principal and Agent roles.
 * 
 * @param {Function} [setNiprNameChangeAccepted=() => {}] - Callback invoked to reset NIPR name-change
 *   acceptance state (e.g. close the confirmation modal) when a new initialization starts.
 * @param {Object} [extraAgencyQuestions={}] - Additional question/answer pairs to merge into the
 *   agency interview initialization payload. Use this to pass cobranded or account-specific
 *   questions that are not part of the standard form (e.g. `{ cobrandedNationalAccount: "IronpeakServicesCorp" }`).
 * @param {Function|null} [onUnhandledError=null] - Optional callback invoked with the raw error when
 *   the initialize API call throws an unexpected error that is not otherwise handled by the built-in
 *   error-state flags. Receives the caught error as its only argument. When `null`, unhandled errors
 *   are silently swallowed.
 *
 * @returns {{
 *   initializeInterviews: Function,
 *   initializing: boolean,
 *   duplicateProfile: boolean,
 *   npnNotFoundInPdb: boolean,
 *   feinNotFoundInPdb: boolean,
 *   producerNameMismatch: boolean,
 *   agencyNameMismatch: boolean,
 *   duplicateAgent: boolean,
 *   duplicateAgency: boolean,
 *   niprAgentNameChange: Object|null,
 *   niprAgencyNameChange: Object|null,
 *   agentAssessmentKickout: boolean,
 *   agencyAssessmentKickout: boolean,
 *   parentAgencyMustOnboard: boolean,
 *   agencyOffshoreMustBeApproved: boolean
 * }} Hook state and functions
 *
 * @property {Function} initializeInterviews - Function to start the interview initialization process
 * @property {boolean} initializing - Flag indicating if initialization is in progress
 * @property {boolean} duplicateProfile - Flag indicating if a duplicate profile was detected
 * @property {boolean} npnNotFoundInPdb - Flag indicating if NPN was not found in PDB
 * @property {boolean} feinNotFoundInPdb - Flag indicating if FEIN was not found in PDB
 * @property {boolean} producerNameMismatch - Flag indicating producer name mismatch with PDB
 * @property {boolean} agencyNameMismatch - Flag indicating agency name mismatch with PDB
 * @property {boolean} duplicateAgent - Flag indicating if duplicate agent interview was detected
 * @property {boolean} duplicateAgency - Flag indicating if duplicate agency interview was detected
 * @property {Object|null} niprAgentNameChange - Object containing agent name change details from NIPR
 * @property {Object|null} niprAgencyNameChange - Object containing agency name change details from NIPR
 * @property {boolean} agentAssessmentKickout - Flag indicating if agent failed assessment validation
 * @property {boolean} agencyAssessmentKickout - Flag indicating if agency failed assessment validation
 * @property {boolean} parentAgencyMustOnboard - Flag indicating if parent agency must onboard first
 * @property {boolean} agencyOffshoreMustBeApproved - Flag indicating if offshore agency must be approved before proceeding
 *
 * @example
 * // Standard usage (no extra questions, default error handling)
 * const {
 *   initializeInterviews,
 *   initializing,
 *   duplicateProfile,
 *   niprAgentNameChange
 * } = useInterviewInitializer(setNiprNameChangeAccepted);
 *
 * @example
 * // Cobranded national account usage with extra agency questions and custom error handler
 * const {
 *   initializeInterviews,
 *   initializing,
 *   duplicateProfile,
 *   niprAgentNameChange
 * } = useInterviewInitializer(
 *   setNiprNameChangeAccepted,
 *   { cobrandedNationalAccount: "IronpeakServicesCorp" },
 *   (err) => console.error('Unhandled initialization error', err)
 * );
 *
 * // Start initialization with form data
 * initializeInterviews({
 *   role: 'Principal',
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   npn: '12345678',
 *   agencyName: 'Example Agency'
 * });
 */
export default function useInterviewInitializer(
    setNiprNameChangeAccepted = () => { },
    extraAgencyQuestions = {},
    onUnhandledError = null
) {
    const {
        setAgentInterview,
        agencyInterview,
        setAgencyInterview,
        setBranchInterview1,
        setBranchInterview2
    } = useContext(GeaoContext);

    const [formData, setFormData] = useState();
    const [initializing, setInitializing] = useState(false);

    const [duplicateProfile, setDuplicateProfile] = useState(false);
    const [npnNotFoundInPdb, setNpnNotFoundInPdb] = useState(false);
    const [feinNotFoundInPdb, setFeinNotFoundInPdb] = useState(false);
    const [producerNameMismatch, setProducerNameMismatch] = useState(false);
    const [agencyNameMismatch, setAgencyNameMismatch] = useState(false);
    const [parentAgencyMustOnboard, setParentAgencyMustOnboard] = useState(false);
    const [agencyOffshoreMustBeApproved, setAgencyOffshoreMustBeApproved] = useState(false);
    const [duplicateAgent, setDuplicateAgent] = useState(false);
    const [duplicateAgency, setDuplicateAgency] = useState(false);

    const [niprAgentNameChange, setNiprAgentNameChange] = useState();
    const [niprAgencyNameChange, setNiprAgencyNameChange] = useState();

    const [agentAssessmentKickout, setAgentAssessmentKickout] = useState(false);
    const [agencyAssessmentKickout, setAgencyAssessmentKickout] = useState(false);

    /**
     * Clear all interviews from session storage on page load.
     */
    useEffect(() => {
        setAgentInterview(null);
        setAgencyInterview(null);
        setBranchInterview1(null);
        setBranchInterview2(null);
    }, []);

    /**
     * On receiving form data, begin initializing the interviews.
     */
    useEffect(() => {
        if (!formData) return;

        // For principal, start with the agency interview.
        if (formData.role === Role.Principal) {
            // If the agency interview is already initialized, don't reinitialize.
            // The agent initialize will be kicked off in its own effect when formData is changed.
            if (agencyInterview?.status !== InterviewStatus.Active) {
                initializeAgencyInterview(formData);
            }
        }
        else {
            initializeAgentInterview(formData);
        }
    }, [formData, agencyInterview]);

    /**
     * For principals, initialize the agent interview after agency has been initialized. This will
     * also trigger when new formData is received in cases where the agency interview was initialized
     * but the agent interview failed due to a need for data to be corrected (ie, PDB name mismatch).
     */
    useEffect(() => {
        if (!formData || !agencyInterview) return;

        if (
            formData.role === Role.Principal &&
            agencyInterview.status === InterviewStatus.Active
        ) {
            initializeAgentInterview(formData);
        }
    }, [formData, agencyInterview]);

    /**
     * Initialize the interviews from the form data.
     * 
     * This function serves as the main entry point for starting the interview initialization
     * process. It resets all error states and determines whether to initialize agency or
     * agent interviews based on the user's role.
     * 
     * @param {Object} formData - The form data containing user inputs
     * @param {string} formData.role - The user's role (Principal or Agent)
     * @param {string} formData.firstName - User's first name
     * @param {string} formData.lastName - User's last name
     * @param {string} formData.npn - National Producer Number
     * @param {string} formData.agencyName - Name of the agency
     * @param {string} formData.fein - Federal Employer Identification Number
     * @param {string} [formData.middleName] - User's middle name
     * @param {string} [formData.suffix] - User's name suffix
     * @param {string} [formData.businessEmail] - Business email address
     * @param {boolean} [formData.offshoreResident] - Whether the user is an offshore resident
     * @param {string} [formData.mobilePhoneCountryCode] - Country code for mobile phone (required if offshoreResident is true)
     * @param {string} [formData.businessPhoneCountryCode] - Country code for business phone (required if offshoreResident is true)
     * @param {string} [formData.businessPhone] - Business phone number
     * @param {string} [formData.mobilePhone] - Mobile phone number
     * @param {boolean} [formData.textOptIn] - Text message opt-in preference
     * @param {string} [formData.soleProprietor] - Whether agency is sole proprietor
     * @param {Array} [formData.geicoAppointedStates] - List of GEICO appointed states
     * @param {boolean} [formData.lobCommercial] - Commercial line of business flag
     * @param {boolean} [formData.lobPersonal] - Personal line of business flag
     * @param {boolean} [formData.activeEoPolicy] - Active E&O policy flag
     * @param {boolean} [formData.intendEoPolicy] - Intent to obtain E&O policy flag
     * @param {boolean} [formData.captiveAgent] - Captive agent flag
     * @param {string} [formData.captiveCarrier] - Captive carrier name
     * @param {string} [formData.captiveCarrierOther] - Other captive carrier name
     */
    function initializeInterviews(formData) {
        if (!initializing) {
            setFormData(formData);
            setInitializing(true);

            setDuplicateProfile(false);
            setNpnNotFoundInPdb(false);
            setFeinNotFoundInPdb(false);
            setProducerNameMismatch(false);
            setAgencyNameMismatch(false);
            setParentAgencyMustOnboard(false);
            setAgencyOffshoreMustBeApproved(false);
            setDuplicateAgent(false);
            setDuplicateAgency(false);

            setNiprAgentNameChange(null);
            setNiprAgencyNameChange(null);
            setNiprNameChangeAccepted(false);

            setAgentAssessmentKickout(false);
            setAgencyAssessmentKickout(false);
        }
        else {
            console.log("Attempted to initialize before the last attempt finished.");
        }
    }

    /**
     * Initialize the agency interview with form values.
     * 
     * Creates and submits an agency interview with the provided form data.
     * Handles success scenarios (including NIPR name changes and assessment kickouts)
     * and various error conditions (duplicates, validation errors).
     * 
     * @param {Object} formData - Form values for agency initialization
     * @param {string} formData.agencyName - Name of the agency
     * @param {string} formData.soleProprietor - Whether agency is sole proprietor ("true"/"false")
     * @param {string} formData.npn - National Producer Number (for sole proprietors)
     * @param {string} formData.fein - Federal Employer Identification Number
     * @param {Array} formData.geicoAppointedStates - List of GEICO appointed states
     * @param {boolean} formData.lobCommercial - Commercial line of business flag
     * @param {boolean} formData.lobPersonal - Personal line of business flag
     * @param {boolean} formData.activeEoPolicy - Active E&O policy flag
     * @param {boolean} formData.intendEoPolicy - Intent to obtain E&O policy flag
     * 
     * @returns {Promise} Promise that resolves when agency interview is initialized and/or error states are set.
     */
    function initializeAgencyInterview(formData) {
        const questionAnswers = objectToQuestionAnswers({
            AgencyName: formData.agencyName,
            SoleProprietor: formData.soleProprietor,
            SolePropPrincipalNpn: formData.soleProprietor === "true" ? formData.npn : null,
            Fein: formData.fein,
            GeicoAppointedStates: formData.geicoAppointedStates,
            LobCommercial: formData.lobCommercial,
            LobPersonal: formData.lobPersonal,
            ActiveEoPolicy: formData.activeEoPolicy,
            IntendEoPolicy: formData.intendEoPolicy,
            ...extraAgencyQuestions
        });

        return initialize({
            identityType: "Agency",
            interview: {
                status: InterviewStatus.Active,
                questionAnswers: questionAnswers
            }
        }).then(responseAgencyInterview => {
            setAgencyInterview(responseAgencyInterview);

            // Check for National Insurance Producer Registery name change.
            if (getAnswer(responseAgencyInterview, "AgencyName") !== formData.agencyName) {
                setNiprAgencyNameChange({
                    enteredAgencyName: formData.agencyName,
                    updatedAgencyName: getAnswer(responseAgencyInterview, "AgencyName")
                });
            }

            // Check for assessment kickouts.
            if (
                responseAgencyInterview.assessments?.HasErrorsAndOmmissions?.status === "Rejected" ||
                responseAgencyInterview.assessments?.NotOnDoNotAppointList?.status === "Rejected" ||
                responseAgencyInterview.assessments?.StateLineOfBusinessOpen?.status === "Rejected"
            ) {
                setAgencyAssessmentKickout(true);
            }
        }).catch(error => {
            const knownValCodes = ["Val-112", "Val-113", "Val-114", "Val-115", "Val-116"];
            let knownError = false;

            if (error instanceof HttpStatusError && error.response.status === 409) {
                setDuplicateAgency(true);
                knownError = true;
            }

            if (error instanceof HttpStatusError) {
                setDuplicateProfile(error.body?.detail === "Val-112");
                setNpnNotFoundInPdb(error.body?.detail === "Val-113");
                setFeinNotFoundInPdb(error.body?.detail === "Val-114");
                setProducerNameMismatch(error.body?.detail === "Val-115");
                setAgencyNameMismatch(error.body?.detail === "Val-116");
                if (knownValCodes.includes(error.body?.detail)) {
                    knownError = true;
                }
            }

            if (!knownError) {
                onUnhandledError?.();
            }

            setInitializing(false);
        });
    }

    /**
     * Initialize the agent interview with form values.
     * 
     * Creates and submits an agent interview with the provided form data.
     * Establishes relationship with parent agency if available.
     * Handles success scenarios (including NIPR name changes and assessment kickouts)
     * and various error conditions (duplicates, validation errors).
     * 
     * @param {Object} formData - Form values for agent initialization
     * @param {string} formData.firstName - Agent's first name
     * @param {string} formData.lastName - Agent's last name
     * @param {string} [formData.middleName] - Agent's middle name
     * @param {string} [formData.suffix] - Agent's name suffix
     * @param {string} formData.businessEmail - Business email address
     * @param {boolean} formData.offshoreResident - Whether the agent is an offshore resident
     * @param {string} [formData.mobilePhoneCountryCode] - Country code for mobile phone (required if offshoreResident is true)
     * @param {string} [formData.businessPhoneCountryCode] - Country code for business phone (required if offshoreResident is true)
     * @param {string} [formData.businessPhone] - Business phone number
     * @param {string} [formData.mobilePhone] - Mobile phone number
     * @param {boolean} [formData.textOptIn] - Text message opt-in preference
     * @param {string} formData.role - Agent's role (Principal/Agent)
     * @param {string} formData.npn - National Producer Number
     * @param {string} formData.fein - Federal Employer Identification Number
     * @param {boolean} [formData.captiveAgent] - Captive agent flag
     * @param {string} [formData.captiveCarrier] - Captive carrier name
     * @param {string} [formData.captiveCarrierOther] - Other captive carrier name
     * 
     * @returns {Promise} Promise that resolves when agent interview is initialized and/or error states are set.
     */
    function initializeAgentInterview(formData) {
        const questionAnswers = objectToQuestionAnswers({
            FirstName: formData.firstName,
            MiddleName: formData.middleName,
            LastName: formData.lastName,
            Suffix: formData.suffix,
            OffshoreResident: formData.offshoreResident,
            BusinessEmailAddress: formData.businessEmail,
            BusinessPhoneCountryCode: formData.businessPhoneCountryCode,
            BusinessPhoneNumber: formData.businessPhone,
            MobilePhoneCountryCode: formData.mobilePhoneCountryCode,
            MobilePhoneNumber: formData.mobilePhone,
            TextOptIn: formData.textOptIn,
            Role: formData.role,
            Npn: formData.npn,
            Fein: formData.fein,
            CaptiveAgent: formData.captiveAgent,
            CaptiveCarrier: formData.captiveCarrier === "other" ?
                formData.captiveCarrierOther :
                formData.captiveCarrier
        });

        const relationships = agencyInterview != null ?
            [{ type: "ParentAgency", interviewId: agencyInterview.interviewId }] :
            [];

        return initialize({
            identityType: "Agent",
            interview: {
                status: InterviewStatus.Active,
                questionAnswers,
                relationships
            }
        }).then(responseAgentInterview => {
            setAgentInterview(responseAgentInterview);

            // Check for National Insurance Producer Registery name change.
            if (
                getAnswer(responseAgentInterview, "FirstName") !== formData.firstName ||
                getAnswer(responseAgentInterview, "LastName") !== formData.lastName
            ) {
                setNiprAgentNameChange({
                    enteredFirstName: formData.firstName,
                    enteredLastName: formData.lastName,
                    updatedFirstName: getAnswer(responseAgentInterview, "FirstName"),
                    updatedLastName: getAnswer(responseAgentInterview, "LastName")
                });
            }

            // Check for assessment kickouts.
            if (
                responseAgentInterview.assessments?.NotACaptiveAgent?.status === "Rejected" ||
                responseAgentInterview.assessments?.NotOnDoNotAppointList?.status === "Rejected"
            ) {
                setAgentAssessmentKickout(true);
            }

            setInitializing(false);
        }).catch(error => {
            const knownValCodes = ["Val-112", "Val-113", "Val-114", "Val-115", "Val-116", "Val-118", "Val-120"];
            let knownError = false;

            if (error instanceof HttpStatusError && error.response.status === 409) {
                setDuplicateAgent(true);
                knownError = true;
            }

            if (error instanceof HttpStatusError) {
                setDuplicateProfile(error.body?.detail === "Val-112");
                setNpnNotFoundInPdb(error.body?.detail === "Val-113");
                setFeinNotFoundInPdb(error.body?.detail === "Val-114");
                setProducerNameMismatch(error.body?.detail === "Val-115");
                setAgencyNameMismatch(error.body?.detail === "Val-116");
                setParentAgencyMustOnboard(error.body?.detail === "Val-118");
                setAgencyOffshoreMustBeApproved(error.body?.detail === "Val-120");
                if (knownValCodes.includes(error.body?.detail)) {
                    knownError = true;
                }
            }

            if (!knownError) {
                onUnhandledError?.();
            }

            setInitializing(false);
        });
    }

    return {
        initializeInterviews,
        initializing,
        duplicateProfile,
        npnNotFoundInPdb,
        feinNotFoundInPdb,
        producerNameMismatch,
        agencyNameMismatch,
        duplicateAgent,
        duplicateAgency,
        niprAgentNameChange,
        niprAgencyNameChange,
        agentAssessmentKickout,
        agencyAssessmentKickout,
        parentAgencyMustOnboard,
        agencyOffshoreMustBeApproved
    };
}